import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteIcon from "@mui/icons-material/Delete";
import SaveIcon from "@mui/icons-material/Save";
import ScoreIcon from "@mui/icons-material/Score";
import { Box, Card, CardContent, Checkbox, Chip, FormControlLabel, Grid, IconButton, Pagination, TextField, Typography } from "@mui/material";
import Alert from "@mui/material/Alert";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import validator from "validator";
import ApiService from "../../../services/ApiService";
import Logger from "../../../services/Logger";
import ColorService from "../../../services/ColorService";
import UtilService from "../../../services/UtilService";
import { getPuzzleTypeById } from "../../pzl_common/PuzzleTypeConstants";
import AdminCrossBoardService from "../../pzl_cross/board/AdminCrossBoardService";
import AdminGridBoardService from "../../pzl_grid/board/AdminGridBoardService";
import ConfirmDialog from "../../components/ConfirmDialog";
import CustomButton from "../../components/CustomButton";
import CustomSnackbar from "../../components/CustomSnackbar";
import Tooltip from "@mui/material/Tooltip";
import WordMeaning from "../../pzl_common/WordMeaning";
import WordNetworkDialog from "../../pzl_common/WordNetworkDialog";
import WordScoreDialog from "../../pzl_common/WordScoreDialog";
import GenreBar from "./GenreBar";
import TopicService from "./TopicService";

export default function TopicDetail() {
    const navigate = useNavigate();
    const location = useLocation();
    const { topicId } = useParams();
    const returnTo = location.state?.returnTo;

    const [title, setTitle] = useState("");
    const [relatedWords, setRelatedWords] = useState("");
    const [created, setCreated] = useState("");
    const [reload, setReload] = useState();
    const [genre, setGenre] = useState("");

    const [errTitle, setErrTitle] = useState(false);
    const [, setErrRelatedWords] = useState(false);

    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [severity, setSeverity] = useState(false);
    const [message, setMessage] = useState("");

    const [openDelete, setOpenDelete] = useState(false);
    const [forceDelete, setForceDelete] = useState(false);
    const [selectedWord, setSelectedWord] = useState("");
    const [templates, setTemplates] = useState([]);
    const [templatePage, setTemplatePage] = useState(1);
    const templatesPerPage = 12; // Show 12 templates per page (4 rows of 3)
    const [difficultyLevel, setDifficultyLevel] = useState("");
    const [original, setOriginal] = useState(null);
    const [gridTpl, setGridTpl] = useState([]);
    const [crossTpl, setCrossTpl] = useState([]);
    const [gridTplPage, setGridTplPage] = useState(1);
    const [crossTplPage, setCrossTplPage] = useState(1);
    const [tags, setTags] = useState([]);
    const [guardianArticleId, setGuardianArticleId] = useState("");
    const [wikipediaArticleId, setWikipediaArticleId] = useState("");
    const [wikipediaCategory, setWikipediaCategory] = useState("");
    const [wikipediaDate, setWikipediaDate] = useState("");
    const [nasaDate, setNasaDate] = useState("");
    const [details, setDetails] = useState(null);
    const [bedrockIdeaId, setBedrockIdeaId] = useState("");
    const [enrichedGridTpl, setEnrichedGridTpl] = useState([]);
    const [enrichedCrossTpl, setEnrichedCrossTpl] = useState([]);
    const [enrichmentRefresh, setEnrichmentRefresh] = useState(0);
    const [scoreDialogTemplate, setScoreDialogTemplate] = useState(null);
    const [networkDialogTemplate, setNetworkDialogTemplate] = useState(null);
    const [wordScoresForDialog, setWordScoresForDialog] = useState([]);
    const [loadingScoresForDialog, setLoadingScoresForDialog] = useState(false);

    useEffect(() => {
        if (topicId) {
            TopicService.requestTopic(topicId).then((r) => {
                if (r) {
                    setTitle(r.title);
                    setRelatedWords(r.relatedWords);
                    setCreated(UtilService.formatDate(r.createdAt));
                    setGenre(r.genre);
                    setDifficultyLevel(r.difficultyLevel || "");
                    setTemplates([]);
                    setOriginal(r.original || null);
                    setGridTpl(r.gridTpl || []);
                    setCrossTpl(r.crossTpl || []);
                    setTemplatePage(1);
                    setGridTplPage(1);
                    setCrossTplPage(1);
                    setTags(Array.isArray(r.tags) ? r.tags : []);
                    setGuardianArticleId(r.guardianArticleId || "");
                    setWikipediaArticleId(r.wikipediaArticleId || "");
                    setWikipediaCategory(r.wikipediaCategory || "");
                    setWikipediaDate(r.wikipediaDate || "");
                    setNasaDate(r.nasaDate ? (typeof r.nasaDate === "string" ? r.nasaDate : r.nasaDate) : "");
                    setDetails(r.details && typeof r.details === "object" ? r.details : null);
                    setBedrockIdeaId(r.bedrockIdeaId || "");
                }
            });
        }
    }, [reload, topicId]);

    // Enrich templates with word network and score from full template API
    useEffect(() => {
        const enrichTemplates = async (templates, fetchFn) => {
            if (!templates || templates.length === 0) return [];
            const results = await Promise.all(
                templates.map(async (tpl) => {
                    try {
                        const full = await fetchFn(tpl.id);
                        if (!full) return { ...tpl };
                        const merged = { ...tpl, ...full, wordNetwork: full.wordNetwork || full.template?.wordNetwork || tpl.wordNetwork };
                        // Fetch word scores if we have words
                        let avgScore = null;
                        const words = (() => {
                            try {
                                const coords = full.filledCoordinates ? JSON.parse(full.filledCoordinates) : [];
                                return coords.map((c) => c.text?.trim()).filter(Boolean);
                            } catch {
                                const wiu = (full.wordsInUse || tpl.wordsInUse || "").split("\n").map((w) => w.trim()).filter(Boolean);
                                return wiu;
                            }
                        })();
                        if (words.length > 0) {
                            try {
                                const lang = full.language || tpl.language || "english";
                                const queryParams = words.map((w) => `words=${encodeURIComponent(w)}`).join("&");
                                const scores = await ApiService.getData(`/home/dic/${lang}/multiple?validOnly=false&${queryParams}`, null);
                                if (Array.isArray(scores) && scores.length > 0) {
                                    const withScore = scores.filter((s) => s.info?.score != null);
                                    if (withScore.length > 0) {
                                        avgScore = Math.round(withScore.reduce((a, s) => a + (s.info?.score || 0), 0) / withScore.length);
                                    }
                                }
                            } catch {
                                /* ignore score fetch errors */
                            }
                        }
                        return { ...merged, averageScore: avgScore ?? merged.averageScore };
                    } catch {
                        return { ...tpl };
                    }
                })
            );
            return results;
        };
        if (gridTpl.length > 0 || crossTpl.length > 0) {
            Promise.all([
                enrichTemplates(gridTpl, AdminGridBoardService.requestHwTpl),
                enrichTemplates(crossTpl, AdminCrossBoardService.requestCrossTpl),
            ]).then(([grid, cross]) => {
                setEnrichedGridTpl(grid);
                setEnrichedCrossTpl(cross);
            });
        } else {
            setEnrichedGridTpl([]);
            setEnrichedCrossTpl([]);
        }
    }, [gridTpl, crossTpl, enrichmentRefresh]);

    const handleUpdate = () => {
        if (validator.isEmpty(title)) {
            setErrTitle(true);
            return;
        }
        setErrTitle(false);

        if (validator.isEmpty(relatedWords)) {
            setErrRelatedWords(true);
            return;
        }
        setErrRelatedWords(false);

        TopicService.updateTopic(topicId, title, relatedWords).then(() => {
            setOpenSnackbar(true);
            setSeverity("info");
            setMessage("updated");
            setReload(moment());
        });
    };

    const handleDelete = () => {
        TopicService.deleteTopic(topicId, forceDelete)
            .then((result) => {
                if (result && (result.code === "failure" || result.code === "FAILURE")) {
                    setSeverity("error");
                    setMessage(result.message || "Failed to delete topic");
                    setOpenSnackbar(true);
                    setOpenDelete(false);
                    return;
                }
                setSeverity("success");
                setMessage("deleted");
                setOpenSnackbar(true);
                navigate("/admin/topic");
            })
            .catch((err) => {
                Logger.error("Error deleting topic:", err);
                setSeverity("error");
                setMessage(err?.message || err?.response?.message || "Failed to delete topic");
                setOpenSnackbar(true);
                setOpenDelete(false);
            });
    };

    const handleWordSelect = (word) => {
        setSelectedWord(word);
    };

    const handleRemoveWord = (wordToRemove) => {
        const wordsArray = relatedWords.split("\n").filter(word => word.trim() !== "");
        const filteredWords = wordsArray.filter(word => word.trim() !== wordToRemove);
        setRelatedWords(filteredWords.join("\n"));
    };

    const handleTemplatePageChange = (_event, value) => {
        setTemplatePage(value);
    };

    const handleGridTplPageChange = (_event, value) => {
        setGridTplPage(value);
    };

    const handleCrossTplPageChange = (_event, value) => {
        setCrossTplPage(value);
    };

    const handleScoreClick = async (e, template) => {
        e.stopPropagation();
        if (!template?.id) return;
        const words = (() => {
            try {
                const coords = template.filledCoordinates ? JSON.parse(template.filledCoordinates) : [];
                return coords.map((c) => c.text?.trim()).filter(Boolean);
            } catch {
                return (template.wordsInUse || "").split("\n").map((w) => w.trim()).filter(Boolean);
            }
        })();
        if (words.length === 0) {
            setOpenSnackbar(true);
            setSeverity("warning");
            setMessage("No words found in puzzle");
            return;
        }
        setScoreDialogTemplate(template);
        setLoadingScoresForDialog(true);
        try {
            const lang = template.language || "english";
            const queryParams = words.map((w) => `words=${encodeURIComponent(w)}`).join("&");
            const result = await ApiService.getData(`/home/dic/${lang}/multiple?validOnly=false&${queryParams}`, null);
            setWordScoresForDialog(Array.isArray(result) ? result : []);
        } catch {
            setWordScoresForDialog([]);
        } finally {
            setLoadingScoresForDialog(false);
        }
    };

    const handleNetworkClick = (e, template) => {
        e.stopPropagation();
        if (!template?.id) return;
        document.body.style.cursor = "default";
        setNetworkDialogTemplate(template);
    };

    // Reset cursor when network dialog opens/closes (fixes stray wait cursor from Cytoscape layout)
    useEffect(() => {
        if (networkDialogTemplate) {
            document.body.style.cursor = "default";
            return () => {
                document.body.style.cursor = "default";
            };
        }
    }, [networkDialogTemplate]);

    const totalTemplatePages = Math.ceil(templates.length / templatesPerPage);
    const startIndex = (templatePage - 1) * templatesPerPage;
    const endIndex = startIndex + templatesPerPage;
    const displayedTemplates = templates.slice(startIndex, endIndex);

    const gridTplToShow = enrichedGridTpl.length > 0 ? enrichedGridTpl : gridTpl;
    const crossTplToShow = enrichedCrossTpl.length > 0 ? enrichedCrossTpl : crossTpl;
    const totalGridTplPages = Math.ceil(gridTplToShow.length / templatesPerPage);
    const gridTplStartIndex = (gridTplPage - 1) * templatesPerPage;
    const gridTplEndIndex = gridTplStartIndex + templatesPerPage;
    const displayedGridTpl = gridTplToShow.slice(gridTplStartIndex, gridTplEndIndex);

    const totalCrossTplPages = Math.ceil(crossTplToShow.length / templatesPerPage);
    const crossTplStartIndex = (crossTplPage - 1) * templatesPerPage;
    const crossTplEndIndex = crossTplStartIndex + templatesPerPage;
    const displayedCrossTpl = crossTplToShow.slice(crossTplStartIndex, crossTplEndIndex);

    const getTemplateStateColor = (template) => {
        return ColorService.getStateColor(template.state);
    };

    const getTemplateStateLabel = (template) => {
        if (!template.state) return null;
        return template.state.split('_').map(word => 
            word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        ).join(' ');
    };

    const getLanguageLabel = (lang) => {
        if (!lang) return null;
        return lang === "french" ? "French" : lang === "english" ? "English" : lang.charAt(0).toUpperCase() + lang.slice(1);
    };

    const getWordNetworkInfo = (template) => {
        const wordNetwork = template?.wordNetwork || template?.template?.wordNetwork;
        if (wordNetwork?.nodes && Array.isArray(wordNetwork.nodes)) {
            return { hasNetwork: true, nodeCount: wordNetwork.nodes.length };
        }
        return { hasNetwork: false, nodeCount: 0 };
    };

    const getTemplateScore = (template) => {
        const score = template?.averageScore ?? template?.score ?? template?.scoreStats?.averageScore;
        return score != null && score !== undefined ? Number(score) : null;
    };

    return (
        <Box sx={{ width: "100%" }}>
            {/* Header Section */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
                <IconButton 
                    onClick={() => navigate(returnTo || "/admin/topic")} 
                    sx={{ 
                        color: 'grey.700',
                        '&:hover': {
                            backgroundColor: 'grey.100'
                        }
                    }}
                >
                    <ArrowBackIcon />
                </IconButton>
                <Box sx={{ flex: 1, display: "flex", alignItems: "center", gap: 2 }}>
                    <GenreBar genre={genre} />
                    {difficultyLevel && (
                        <Chip
                            label={difficultyLevel}
                            size="medium"
                            sx={{
                                backgroundColor: ColorService.getDifficultyColor(difficultyLevel),
                                color: "white",
                                fontWeight: "medium"
                            }}
                        />
                    )}
                </Box>
                <Box sx={{ display: "flex", gap: 1 }}>
                    <IconButton color="warning" onClick={() => setOpenDelete(true)}>
                        <DeleteIcon />
                    </IconButton>
                    <IconButton color="primary" onClick={handleUpdate}>
                        <SaveIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Title Field */}
            <Box sx={{ mb: 3, display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
                <TextField
                    label="Title"
                    variant="outlined"
                    size="small"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    error={errTitle}
                    sx={{ flex: 1, minWidth: 200 }}
                />
                <TextField
                    label="Created"
                    variant="outlined"
                    size="small"
                    value={created}
                    disabled
                    sx={{ width: 180, flexShrink: 0 }}
                />
            </Box>

            {/* Main Content: Related Words and Word Meaning */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
                {/* Left Column: Related Words */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card sx={{ height: "100%" }}>
                        <CardContent>
                            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
                                <Typography variant="h6">Related Words</Typography>
                                <Alert severity="info" sx={{ py: 0, px: 1, fontSize: "0.75rem" }}>
                                    {relatedWords ? relatedWords.split("\n").filter(word => word.trim() !== "").length : 0} words
                                </Alert>
                            </Box>
                            {relatedWords && (
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, maxHeight: 500, overflow: "auto", minHeight: 200 }}>
                                    {relatedWords.split("\n").filter(word => word.trim() !== "").map((word, index) => (
                                        <Chip
                                            key={`${word}-${index}`}
                                            label={word.trim()}
                                            size="small"
                                            variant={selectedWord === word.trim() ? "filled" : "outlined"}
                                            color={selectedWord === word.trim() ? "primary" : "default"}
                                            onClick={() => handleWordSelect(word.trim())}
                                            onDelete={() => handleRemoveWord(word.trim())}
                                            sx={{ cursor: "pointer" }}
                                        />
                                    ))}
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </Grid>

                {/* Right Column: Word Meaning */}
                <Grid size={{ xs: 12, md: 6 }}>
                    <Card sx={{ height: "100%" }}>
                        <CardContent>
                            <Typography variant="h6" gutterBottom>
                                Word Meaning
                            </Typography>
                            {selectedWord ? (
                                <WordMeaning
                                    selectedWord={selectedWord}
                                    onSaveDefinition={() => {}}
                                    gridTplId={topicId}
                                    showClueButton={false}
                                />
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Click a word from the list to see its meaning.
                                </Typography>
                            )}
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {gridTpl.length > 0 && (
                <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid", borderColor: "divider" }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Grid Templates ({gridTpl.length})
                    </Typography>
                    <Grid container spacing={2}>
                        {displayedGridTpl.map((template, index) => {
                            const puzzleType = template.type ? getPuzzleTypeById(template.type) : null;
                            const displayTitle = template.title || template.name || "Grid Template";
                            const wordNetworkInfo = getWordNetworkInfo(template);
                            const templateScore = getTemplateScore(template);
                            return (
                                <Grid key={template.id || index} size={{ xs: 12, sm: 6, md: 4 }}>
                                    <Box
                                        onClick={() => template.id && navigate(`/admin/puzzle/grid_tpl/detail/${template.id}`)}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 1,
                                            border: "1px solid",
                                            borderColor: "divider",
                                            backgroundColor: "background.paper",
                                            cursor: template.id ? "pointer" : "default",
                                            "&:hover": {
                                                backgroundColor: "action.hover",
                                                borderColor: "primary.main"
                                            }
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: "medium",
                                                mb: 0.5,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap"
                                            }}
                                            title={displayTitle}
                                        >
                                            {displayTitle}
                                        </Typography>
                                        {template.name && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                Name: {template.name}
                                            </Typography>
                                        )}
                                        {template.id && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontFamily: "monospace", fontSize: "0.65rem" }}>
                                                {template.id}
                                            </Typography>
                                        )}
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center", mb: 0.5 }}>
                                            {wordNetworkInfo.hasNetwork && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Network: {wordNetworkInfo.nodeCount} nodes
                                                </Typography>
                                            )}
                                            {templateScore != null && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Score: {Number(templateScore).toLocaleString()}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                                            {template.difficultyLevel && (
                                                <Chip
                                                    label={template.difficultyLevel}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: ColorService.getDifficultyColor(template.difficultyLevel),
                                                        color: "white",
                                                        fontSize: "0.7rem",
                                                        height: 22,
                                                        fontWeight: "medium"
                                                    }}
                                                />
                                            )}
                                            {getLanguageLabel(template.language) && (
                                                <Chip
                                                    label={getLanguageLabel(template.language)}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontSize: "0.7rem", height: 22 }}
                                                />
                                            )}
                                            {puzzleType && (
                                                <Chip
                                                    label={puzzleType.name}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontSize: "0.7rem", height: 22 }}
                                                />
                                            )}
                                            {getTemplateStateLabel(template) && (
                                                <Chip
                                                    label={getTemplateStateLabel(template)}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: getTemplateStateColor(template),
                                                        color: "white",
                                                        fontSize: "0.75rem",
                                                        fontWeight: "medium"
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        {template.tags && Array.isArray(template.tags) && template.tags.length > 0 && (
                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                                                {template.tags.map((tag, tagIndex) => (
                                                    <Tooltip key={tagIndex} title="Tag">
                                                        <Chip
                                                            label={typeof tag === "string" ? tag : tag?.tag ?? String(tag)}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ fontSize: "0.65rem", height: 20 }}
                                                        />
                                                    </Tooltip>
                                                ))}
                                            </Box>
                                        )}
                                        <Box sx={{ display: "flex", gap: 0.5, mt: 1, justifyContent: "flex-end" }}>
                                            <Tooltip title="View word scores">
                                                <IconButton size="small" onClick={(e) => handleScoreClick(e, template)} sx={{ color: "#ed6c02" }}>
                                                    <ScoreIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Word network">
                                                <IconButton size="small" onClick={(e) => handleNetworkClick(e, template)}>
                                                    <AccountTreeIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                    {totalGridTplPages > 1 && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                            <Pagination
                                count={totalGridTplPages}
                                page={gridTplPage}
                                onChange={handleGridTplPageChange}
                                color="primary"
                            />
                        </Box>
                    )}
                </Box>
            )}

            {crossTpl.length > 0 && (
                <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid", borderColor: "divider" }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Crossword Templates ({crossTpl.length})
                    </Typography>
                    <Grid container spacing={2}>
                        {displayedCrossTpl.map((template, index) => {
                            const puzzleType = template.type ? getPuzzleTypeById(template.type) : null;
                            const displayTitle = template.title || template.name || "Crossword Template";
                            const wordNetworkInfo = getWordNetworkInfo(template);
                            const templateScore = getTemplateScore(template);
                            return (
                                <Grid key={template.id || index} size={{ xs: 12, sm: 6, md: 4 }}>
                                    <Box
                                        onClick={() => template.id && navigate(`/admin/puzzle/cross_tpl/detail/${template.id}`)}
                                        sx={{
                                            p: 1.5,
                                            borderRadius: 1,
                                            border: "1px solid",
                                            borderColor: "divider",
                                            backgroundColor: "background.paper",
                                            cursor: template.id ? "pointer" : "default",
                                            "&:hover": {
                                                backgroundColor: "action.hover",
                                                borderColor: "primary.main"
                                            }
                                        }}
                                    >
                                        <Typography
                                            variant="body2"
                                            sx={{
                                                fontWeight: "medium",
                                                mb: 0.5,
                                                overflow: "hidden",
                                                textOverflow: "ellipsis",
                                                whiteSpace: "nowrap"
                                            }}
                                            title={displayTitle}
                                        >
                                            {displayTitle}
                                        </Typography>
                                        {template.name && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                                                Name: {template.name}
                                            </Typography>
                                        )}
                                        {template.id && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontFamily: "monospace", fontSize: "0.65rem" }}>
                                                {template.id}
                                            </Typography>
                                        )}
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center", mb: 0.5 }}>
                                            {wordNetworkInfo.hasNetwork && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Network: {wordNetworkInfo.nodeCount} nodes
                                                </Typography>
                                            )}
                                            {templateScore != null && (
                                                <Typography variant="caption" color="text.secondary">
                                                    Score: {Number(templateScore).toLocaleString()}
                                                </Typography>
                                            )}
                                        </Box>
                                        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, alignItems: "center" }}>
                                            {template.difficultyLevel && (
                                                <Chip
                                                    label={template.difficultyLevel}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: ColorService.getDifficultyColor(template.difficultyLevel),
                                                        color: "white",
                                                        fontSize: "0.7rem",
                                                        height: 22,
                                                        fontWeight: "medium"
                                                    }}
                                                />
                                            )}
                                            {getLanguageLabel(template.language) && (
                                                <Chip
                                                    label={getLanguageLabel(template.language)}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontSize: "0.7rem", height: 22 }}
                                                />
                                            )}
                                            {puzzleType && (
                                                <Chip
                                                    label={puzzleType.name}
                                                    size="small"
                                                    variant="outlined"
                                                    sx={{ fontSize: "0.7rem", height: 22 }}
                                                />
                                            )}
                                            {getTemplateStateLabel(template) && (
                                                <Chip
                                                    label={getTemplateStateLabel(template)}
                                                    size="small"
                                                    sx={{
                                                        backgroundColor: getTemplateStateColor(template),
                                                        color: "white",
                                                        fontSize: "0.75rem",
                                                        fontWeight: "medium"
                                                    }}
                                                />
                                            )}
                                        </Box>
                                        {template.tags && Array.isArray(template.tags) && template.tags.length > 0 && (
                                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 0.5 }}>
                                                {template.tags.map((tag, tagIndex) => (
                                                    <Tooltip key={tagIndex} title="Tag">
                                                        <Chip
                                                            label={typeof tag === "string" ? tag : tag?.tag ?? String(tag)}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ fontSize: "0.65rem", height: 20 }}
                                                        />
                                                    </Tooltip>
                                                ))}
                                            </Box>
                                        )}
                                        <Box sx={{ display: "flex", gap: 0.5, mt: 1, justifyContent: "flex-end" }}>
                                            <Tooltip title="View word scores">
                                                <IconButton size="small" onClick={(e) => handleScoreClick(e, template)} sx={{ color: "#ed6c02" }}>
                                                    <ScoreIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Word network">
                                                <IconButton size="small" onClick={(e) => handleNetworkClick(e, template)}>
                                                    <AccountTreeIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </Box>
                                    </Box>
                                </Grid>
                            );
                        })}
                    </Grid>
                    {totalCrossTplPages > 1 && (
                        <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                            <Pagination
                                count={totalCrossTplPages}
                                page={crossTplPage}
                                onChange={handleCrossTplPageChange}
                                color="primary"
                            />
                        </Box>
                    )}
                </Box>
            )}

            {(tags.length > 0 || guardianArticleId || wikipediaArticleId || wikipediaCategory || wikipediaDate || nasaDate || (details && Object.keys(details).length > 0) || bedrockIdeaId) && (
                <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid", borderColor: "divider" }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        More details
                    </Typography>
                    <Grid container spacing={2}>
                        {tags.length > 0 && (
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    Tags
                                </Typography>
                                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                                    {tags.map((tag, index) => (
                                        <Chip key={`${tag}-${index}`} label={tag} size="small" variant="outlined" />
                                    ))}
                                </Box>
                            </Grid>
                        )}
                        {bedrockIdeaId && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Bedrock Idea ID
                                </Typography>
                                <Typography variant="body1" sx={{ wordBreak: "break-all" }}>
                                    {bedrockIdeaId}
                                </Typography>
                            </Grid>
                        )}
                        {guardianArticleId && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Guardian Article ID
                                </Typography>
                                <Typography variant="body1" sx={{ wordBreak: "break-all" }}>
                                    {guardianArticleId}
                                </Typography>
                            </Grid>
                        )}
                        {wikipediaArticleId && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Wikipedia Article ID
                                </Typography>
                                <Typography variant="body1" sx={{ wordBreak: "break-all" }}>
                                    {wikipediaArticleId}
                                </Typography>
                            </Grid>
                        )}
                        {wikipediaCategory && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Wikipedia Category
                                </Typography>
                                <Typography variant="body1">
                                    {wikipediaCategory}
                                </Typography>
                            </Grid>
                        )}
                        {wikipediaDate && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Wikipedia Date
                                </Typography>
                                <Typography variant="body1">
                                    {wikipediaDate}
                                </Typography>
                            </Grid>
                        )}
                        {nasaDate && (
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <Typography variant="body2" color="text.secondary">
                                    NASA Date
                                </Typography>
                                <Typography variant="body1">
                                    {String(nasaDate)}
                                </Typography>
                            </Grid>
                        )}
                        {details && Object.keys(details).length > 0 && (
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                                    Details
                                </Typography>
                                <Box
                                    component="pre"
                                    sx={{
                                        p: 1.5,
                                        borderRadius: 1,
                                        bgcolor: "grey.100",
                                        overflow: "auto",
                                        fontSize: "0.875rem",
                                        maxHeight: 300
                                    }}
                                >
                                    {JSON.stringify(details, null, 2)}
                                </Box>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            )}

            {original && original.wiki && (
                <Box sx={{ mt: 4, pt: 3, borderTop: "1px solid", borderColor: "divider" }}>
                    <Typography variant="h6" sx={{ mb: 2 }}>
                        Wikipedia Source
                    </Typography>
                    <Grid container spacing={2}>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="body2" color="text.secondary">
                                Date:
                            </Typography>
                            <Typography variant="body1">
                                {original.wiki.date} {original.wiki.year}
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <Typography variant="body2" color="text.secondary">
                                Title:
                            </Typography>
                            <Typography variant="body1">
                                {original.wiki.title}
                            </Typography>
                        </Grid>
                        {original.url && (
                            <Grid size={{ xs: 12 }}>
                                <Typography variant="body2" color="text.secondary">
                                    URL:
                                </Typography>
                                <Typography 
                                    variant="body1" 
                                    component="a" 
                                    href={original.url} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    sx={{ 
                                        color: "primary.main", 
                                        textDecoration: "underline",
                                        wordBreak: "break-all"
                                    }}
                                >
                                    {original.url}
                                </Typography>
                            </Grid>
                        )}
                    </Grid>
                </Box>
            )}

            <WordScoreDialog
                open={!!scoreDialogTemplate}
                onClose={() => setScoreDialogTemplate(null)}
                wordScores={wordScoresForDialog}
                loadingScores={loadingScoresForDialog}
                language={scoreDialogTemplate?.language || "english"}
                onRefresh={async () => {
                    if (scoreDialogTemplate) {
                        setLoadingScoresForDialog(true);
                        try {
                            const words = (() => {
                                try {
                                    const coords = scoreDialogTemplate.filledCoordinates ? JSON.parse(scoreDialogTemplate.filledCoordinates) : [];
                                    return coords.map((c) => c.text?.trim()).filter(Boolean);
                                } catch {
                                    return (scoreDialogTemplate.wordsInUse || "").split("\n").map((w) => w.trim()).filter(Boolean);
                                }
                            })();
                            const lang = scoreDialogTemplate.language || "english";
                            const queryParams = words.map((w) => `words=${encodeURIComponent(w)}`).join("&");
                            const result = await ApiService.getData(`/home/dic/${lang}/multiple?validOnly=false&${queryParams}`, null);
                            setWordScoresForDialog(Array.isArray(result) ? result : []);
                        } finally {
                            setLoadingScoresForDialog(false);
                        }
                    }
                }}
            />

            <WordNetworkDialog
                open={!!networkDialogTemplate}
                onClose={() => setNetworkDialogTemplate(null)}
                hasWordNetwork={!!(networkDialogTemplate && getWordNetworkInfo(networkDialogTemplate).hasNetwork)}
                wordNetworkData={networkDialogTemplate ? (networkDialogTemplate.wordNetwork || networkDialogTemplate.template?.wordNetwork) : null}
                onDeleteWord={undefined}
                onNodeSelect={undefined}
                onRefresh={undefined}
                onGenerateWordNetwork={async () => {
                    if (!networkDialogTemplate?.id) return;
                    const isGrid = gridTpl.some((t) => t.id === networkDialogTemplate.id);
                    setNetworkDialogTemplate(null);
                    try {
                        if (isGrid) {
                            await AdminGridBoardService.generateWordNetwork(networkDialogTemplate.id);
                        } else {
                            await AdminCrossBoardService.generateWordNetwork(networkDialogTemplate.id);
                        }
                        setOpenSnackbar(true);
                        setSeverity("success");
                        setMessage("Word network generated");
                        setEnrichmentRefresh((r) => r + 1);
                    } catch (err) {
                        setOpenSnackbar(true);
                        setSeverity("error");
                        setMessage(err?.message || "Failed to generate word network");
                    }
                }}
            />

            <CustomSnackbar open={openSnackbar} setOpen={setOpenSnackbar} severity={severity} message={message} />

            <ConfirmDialog
                open={openDelete}
                setOpen={(open) => {
                    setOpenDelete(open);
                    if (!open) setForceDelete(false);
                }}
                title="Are you sure you want to delete this topic?"
                message="This action cannot be undone."
                warningAlert="Its related grid and crossword templates are going to delete as well but if there is the puzzles history, error will be returned."
                targetName={`'${title}' will be deleted`}
                callback={handleDelete}
                extraContent={
                    <Box sx={{ mt: 2 }}>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    checked={forceDelete}
                                    onChange={(e) => setForceDelete(e.target.checked)}
                                    color="primary"
                                />
                            }
                            label="Force (delete play history together)"
                        />
                    </Box>
                }
            />
        </Box>
    );
}
